/**
 * Obsidian Amber Dark Theme
 *
 * Warm dark surfaces with amber primary actions.
 */
// Text tones the sidebar shares with the main ladder, declared once so raising
// one raises both. These were copied hex values, and a contrast fix to the
// ladder left the sidebar behind without anything failing.
const TEXT_HEADING = '#FFF3DE';
const TEXT_SECONDARY = '#D6C2A8';

const obsidianAmberDarkTheme = {
  name: 'obsidian-amber-dark',
  label: 'Obsidian Amber Dark',
  mode: 'dark',
  colorSet: 'obsidian-amber',

  colors: {
    // Background elevation system
    canvas: '#0C0804',
    panelPrimary: '#221A10',
    panelSecondary: '#2E2216',
    panelTertiary: '#362A1C',
    panelQuaternary: '#4A3828',
    surfaceHover: '#3E3020',

    // Borders
    borderSubtle: '#3E3020',
    borderStrong: '#6A4A2A',

    // Text hierarchy
    textHeading: TEXT_HEADING,
    textPrimary: '#F5E6D0',
    textSecondary: TEXT_SECONDARY,
    textSupporting: '#B89572',
    textNeutral: '#AC9074',
    textMuted: '#B08E6B',
    textInverse: '#0C0804',

    // Primary accent: amber
    accentPrimary: '#D97706',
    accentPrimaryVibrant: '#FCD34D',

    // Secondary accent: green
    accentSecondary: '#4ADE80',
    accentSecondaryVibrant: '#86EFAC',

    // Warm accent: CTA and warning amber
    accentWarm: '#D97706',
    accentWarmVibrant: '#FCD34D',

    // Status colors
    statusDanger: '#F87171',
    statusWarning: '#FCD34D',
    statusSuccess: '#4ADE80',
    statusInfo: '#F59E0B',

    // Support colors
    gridline: 'rgba(252, 211, 77, 0.14)',
    focusOutline: 'rgba(217, 119, 6, 0.65)',
    overlay: 'rgba(12, 8, 4, 0.80)',

    // Component-specific
    headerBg: '#120E06',
    sidebarBg: '#0C0804',
    cardBg: '#221A10',
    inputBg: '#120E06',
    dropdownBg: '#221A10',

    // Navigation sidebar
    navSectionHeader: '#A08060',
    navItemText: TEXT_SECONDARY,
    navItemHover: TEXT_HEADING,
    navItemHoverBg: 'rgba(217, 119, 6, 0.12)',
    navItemActive: '#FCD34D',
    navItemActiveBg: 'rgba(217, 119, 6, 0.18)',
    navIconDefault: '#A08060',
    navIconHover: '#FCD34D',
    navIconActive: '#FCD34D',
    navDivider: 'rgba(160, 128, 96, 0.22)',

    // Table cell colors
    tableEntity: '#F5E6D0',
    tableMetric: '#86EFAC',
    tableBody: '#D6C2A8',
    tableSecondary: '#B89572',
    tableTime: '#A08060',

    // Quality distribution colors
    qualityP20: '#F87171',
    qualityP40: '#FB923C',
    qualityP60: '#FCD34D',
    qualityP80: '#86EFAC',
    qualityP100: '#4ADE80',

    // Pill styling - elevated backgrounds
    pillBgFrom: '#2E2216',
    pillBgTo: '#362A1C',
    pillActiveBgFrom: '#3E3020',
    pillActiveBgTo: '#4A3828',
    pillHoverBgFrom: '#362A1C',
    pillHoverBgTo: '#3E3020',

    // Table-specific
    tableHeaderBg: '#2E2216',
    tableRowHover: 'rgba(217, 119, 6, 0.10)',
    tableRowExpanded: 'rgba(74, 222, 128, 0.09)',
    buttonTextOnAccent: '#0F1115',

    // RGB channels for opacity modifiers
    statusDangerRgb: '248, 113, 113',
    statusSuccessRgb: '74, 222, 128',
    statusWarningRgb: '252, 211, 77',
    statusInfoRgb: '245, 158, 11',
    accentWarmRgb: '217, 119, 6',

    // Severity
    severityCriticalText: '#F87171',
    severityCriticalBg: 'rgba(248, 113, 113, 0.13)',
    severityCriticalBorder: 'rgba(248, 113, 113, 0.34)',
    severityCriticalGlow: '0 0 18px rgba(248, 113, 113, 0.28)',
    severityHighText: '#FCD34D',
    severityHighBg: 'rgba(252, 211, 77, 0.13)',
    severityHighBorder: 'rgba(252, 211, 77, 0.34)',
    severityHighGlow: '0 0 18px rgba(252, 211, 77, 0.28)',
    severityMediumText: '#D97706',
    severityMediumBg: 'rgba(217, 119, 6, 0.13)',
    severityMediumBorder: 'rgba(217, 119, 6, 0.34)',
    severityMediumGlow: '0 0 18px rgba(217, 119, 6, 0.28)',
    severityLowText: '#F59E0B',
    severityLowBg: 'rgba(245, 158, 11, 0.13)',
    severityLowBorder: 'rgba(245, 158, 11, 0.34)',
    severityLowGlow: '0 0 18px rgba(245, 158, 11, 0.28)',
    severitySuccessText: '#4ADE80',
    severitySuccessBg: 'rgba(74, 222, 128, 0.13)',
    severitySuccessBorder: 'rgba(74, 222, 128, 0.34)',
    severitySuccessGlow: '0 0 8px rgba(74, 222, 128, 0.28)',

    // Border hierarchy
    borderMuted: '#3E3020',
    borderMedium: '#4A3828',
    borderAccent: '#6A4A2A',

    // Surface overlays - subtle layering
    surfaceSubtle: 'rgba(245, 230, 208, 0.045)',
    surfaceMid: 'rgba(245, 230, 208, 0.075)',
    surfaceOverlayHover: 'rgba(217, 119, 6, 0.085)',

    // Scrollbar
    scrollbarTrack: 'rgba(54, 42, 28, 0.42)',
    scrollbarThumb: 'rgba(160, 128, 96, 0.48)',
    scrollbarThumbHover: 'rgba(214, 194, 168, 0.62)',

    // Selection
    selectionBg: 'rgba(217, 119, 6, 0.30)',

    // Graph visualization
    graphBg: '#0C0804',
    graphNodeBg: '#221A10',
    graphNodeText: '#F5E6D0',
  },

  shadows: {
    card: '0 1px 2px rgba(0, 0, 0, 0.36), 0 12px 28px rgba(0, 0, 0, 0.42)',
    soft: '0 4px 16px rgba(0, 0, 0, 0.34), 0 18px 40px rgba(0, 0, 0, 0.38)',
    dropdown: '0 12px 32px rgba(0, 0, 0, 0.50), 0 1px 3px rgba(0, 0, 0, 0.42)',
    drawer: '-8px 0 24px rgba(0, 0, 0, 0.44), -1px 0 3px rgba(0, 0, 0, 0.36)',
  },
};

export default obsidianAmberDarkTheme;
